import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { SuperAdminBookingListItemDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';
import {
    formatBoolean,
    formatDateOnly,
    formatDateTime,
    formatNavValue,
} from '@/features/superAdmin/features/data/shared/utils/superAdminDataFormatters';
import {
    getBookingStatusLabel,
    getDocumentTypeLabel,
    getEventTypeLabel,
} from '@/features/superAdmin/features/data/bookings/utils/superAdminBookingEnumLabels';

type DetailField = {
    label: string;
    value: string;
};

type Props = {
    booking: SuperAdminBookingListItemDto;
};

const SuperAdminBookingMainTab: FC<Props> = ({ booking }) => {
    const { t } = useTranslation();

    const fields: DetailField[] = [
        { label: t('superAdmin.data.bookings.columns.id'), value: booking.id },
        { label: t('superAdmin.data.bookings.columns.number'), value: booking.documentNumber ?? '' },
        { label: t('superAdmin.data.bookings.columns.date'), value: formatDateTime(booking.documentDate) },
        { label: t('superAdmin.data.bookings.columns.tenant'), value: formatNavValue(booking.tenantId, booking.tenantName) },
        { label: t('superAdmin.data.bookings.columns.hotel'), value: formatNavValue(booking.hotelId, booking.hotelName) },
        { label: t('superAdmin.data.bookings.columns.room'), value: formatNavValue(booking.apartmentId, booking.apartmentName) },
        { label: t('superAdmin.data.bookings.columns.owner'), value: formatNavValue(booking.ownerId, booking.ownerName) },
        { label: t('superAdmin.data.bookings.columns.event'), value: getEventTypeLabel(booking.eventType, t) },
        { label: t('superAdmin.data.bookings.columns.type'), value: getDocumentTypeLabel(booking.documentType, t) },
        { label: t('superAdmin.data.bookings.columns.status'), value: getBookingStatusLabel(booking.bookingStatus, t) },
        { label: t('superAdmin.data.bookings.columns.checkIn'), value: formatDateOnly(booking.checkInDate) },
        { label: t('superAdmin.data.bookings.columns.checkOut'), value: formatDateOnly(booking.checkOutDate) },
        { label: t('superAdmin.data.bookings.columns.guest'), value: booking.guestFullName ?? '' },
        { label: t('superAdmin.data.bookings.detail.rooms'), value: String(booking.numberOfRooms) },
        { label: t('superAdmin.data.bookings.columns.amount'), value: String(booking.totalAmount) },
        { label: t('superAdmin.data.bookings.detail.active'), value: formatBoolean(booking.isActive) },
        { label: t('superAdmin.data.bookings.detail.hasAccommodation'), value: formatBoolean(booking.hasAccommodation) },
        { label: t('superAdmin.data.bookings.columns.external'), value: booking.externalId ?? '' },
        { label: t('superAdmin.data.bookings.detail.version'), value: String(booking.version) },
        { label: t('superAdmin.data.bookings.detail.remarks'), value: booking.remarks ?? '' },
    ];

    return (
        <div className="rounded-semi-xl border border-popover-100 bg-popover-10 p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {fields.map((field) => (
                    <div key={field.label} className="flex flex-col gap-1.5">
                        <Label className="text-popover-800">{field.label}</Label>
                        <span className="break-all text-sm">{field.value || '—'}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SuperAdminBookingMainTab;
